import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService, UserRole } from '../auth/auth.service';
import { ScgHttpWrapper } from '../injectables/scg-http-wrapper';
import { DeviceType } from '../models/device';
import { DeviceService } from './device.service';
import { CbSettings, CbSettingsParams, CcSettings, CcSettingsParams, SettingsService } from './settings.service';

type DeviceId = string;
type Username = string;

@Injectable({
  providedIn: 'root'
})
export class RoamingService {
  /** Contains roaming group id or undefined if device/user is not assigned to a roaming group */
  private roamingIdSubjects: Map<DeviceId, Map<Username, BehaviorSubject<number|undefined>>|BehaviorSubject<number|undefined>> = new Map();

  constructor(
    private device: DeviceService,
    private auth: AuthService,
    private settings: SettingsService,
    private log: NGXLogger,
    private scgHttp: ScgHttpWrapper
  ) { }

  public init() {
    this.device.onDeviceSwitch.subscribe((newDevice) => {
      // All roles are allowed to access this
      if (this.auth.role === UserRole.Default || this.auth.role === UserRole.Parents || this.auth.role === UserRole.Report) {

        // Refresh current info for active device if it is already cached
        const subject = this.getRoamingIdSubject(newDevice.device.id, newDevice.username);

        if (subject) {
          this.getRoamingGroupIdFromApi(newDevice.device.id, newDevice.username).subscribe(roamingId => {
            subject.next(roamingId);
          });
        }
      }
    });

    this.auth.onLogout.subscribe(() => {
      for (const [, subjectOrMap] of this.roamingIdSubjects) {
        if (subjectOrMap instanceof Map) {
          for (const [, subject] of subjectOrMap) {
            subject.complete();
          }
        } else {
          subjectOrMap.complete();
        }
      }

      this.roamingIdSubjects.clear();
    });
  }

  public getRoamingGroupId(deviceId: string, username?: string): Observable<number|undefined|never> {
    if (deviceId == null || deviceId === '') {
      this.log.warn('Got empty deviceId');
      return EMPTY;
    }

    const roamingIdSubject = this.getRoamingIdSubject(deviceId, username);

    if (roamingIdSubject) {
      return roamingIdSubject
        .asObservable()
        .pipe(
          // -1 is the initial value if id is still unknown
          filter(id => id !== -1),
          distinctUntilChanged()
        );
    }

    const subject = this.addRoamingIdSubject(deviceId, username);

    this.getRoamingGroupIdFromApi(deviceId, username).subscribe(roamingId => {
      subject.next(roamingId);
    });

    return subject
      .asObservable()
      .pipe(
        // -1 is the initial value if id is still unknown
        filter(id => id !== -1),
        distinctUntilChanged()
      );
  }

  /**
   * Assigns the given device/user to the given roaming group.
   *
   * To remove the assignment to a roaming group, set the id to undefined.
   */
  public saveRoamingGroupId(deviceId: string, username: string, roamingId: number|undefined): Observable<any|never> {
    const device = this.device.devices.find(other => other.id === deviceId);

    if (!device) {
      this.log.warn('Device with given id was not found');
      return EMPTY;
    }

    // We want roamingId to be undefined instead of 0 for better differentiation between assignment and unassignment
    if (roamingId === 0) {
      this.log.warn('Deprecation: Please set the roamingId to undefined instead of 0 to remove a roaming group assignment');
      roamingId = undefined;
    }

    let observable: Observable<any|never> = EMPTY;
    if (device.type === DeviceType.CC && username != null) {
      const ccSettings: CcSettings = {};
      ccSettings.roaming = roamingId != null ? 'F' + roamingId.toString() : '';
      observable = this.settings.setCcSettingsWithDevice(deviceId, username, ccSettings);
    } else if (device.type === DeviceType.CB) {
      const cbSettings: CbSettings = {};
      cbSettings.roaming = roamingId != null ? 'F' + roamingId.toString() : '';
      observable = this.settings.setCbSettingsWithDevice(deviceId, cbSettings);
    }

    return observable.pipe(
      tap(() => {
        const roamingIdSubject = this.getRoamingIdSubject(deviceId, username);

        if (roamingIdSubject) {
          // Update cached data
          roamingIdSubject.next(roamingId);
        }
      })
    );
  }

  public hasRoaming(deviceId: string, username?: string): Observable<boolean|never> {
    return this.getRoamingGroupId(deviceId, username)
      .pipe(
        map<number|undefined|never, boolean|never>(roamingId => roamingId != null)
      );
  }

  /**
   * @param id Id of the roaming group
   * @returns Array containing the device and user names of the members in the roaming group
   */
  public getRoamingGroupMembers(id: number): Observable<string[]> {
    return this.scgHttp.get<string[]>(environment.apiUrl + 'info/getroamingusersforgroup/' + id.toString());
  }

  /**
   * @returns Array containing the names of the roaming groups
   */
  public getRoamingGroupNames(): Observable<string[]> {
    return this.scgHttp.get<string[]>(environment.apiUrl + 'roaming/getallgroupname');
  }

  /**
   * @param id Id of the roaming group, text as name of group
   */
  public setRoamingGroupName(id: number, customText: string): Observable<string[]> {
    const postVal = {groupNo: id, text: customText};
    return this.scgHttp.withoutSync().post<any>(environment.apiUrl + 'roaming/setgroupname', postVal);
  }


  /**
   * Returns id of the roaming group if device/user is assigned to a roaming group.
   * Otherwise, undefined is returned.
   */
  private getRoamingGroupIdFromApi(deviceId: string, username?: string): Observable<number|undefined|never> {
    const device = this.device.devices.find(other => other.id === deviceId);

    if (!device) {
      this.log.warn('Device with given id was not found');
      return EMPTY;
    }

    let observable: Observable<CbSettings|CbSettings|never> = EMPTY;
    if (device.type === DeviceType.CC && username != null) {
      observable = this.settings.getCcSettingsWithDevice(deviceId, username, [ CcSettingsParams.roaming ]);
    } else if (device.type === DeviceType.CB) {
      observable = this.settings.getCbSettingsWithDevice(deviceId, [ CbSettingsParams.roaming ]);
    }

    return observable.pipe(
      map<CcSettings|CbSettings, number|undefined>(response => {
        if (response.roaming != null) {
          const idStr = response.roaming.replace('F', '');
          const id = parseInt(idStr, 10);

          // Checking id > 0 is probably unnecessary
          return id != null && !isNaN(id) && id > 0 ? id : undefined;
        }

        return undefined;
      })
    );
  }

  private getRoamingIdSubject(deviceId: string, username?: string): BehaviorSubject<number|undefined>|undefined {
    const deviceRoamingIdSubjects = this.roamingIdSubjects.get(deviceId);

    if (deviceRoamingIdSubjects) {
      if (username != null && deviceRoamingIdSubjects instanceof Map) {
        return deviceRoamingIdSubjects.get(username);
      } else if (username == null && deviceRoamingIdSubjects instanceof BehaviorSubject) {
        return deviceRoamingIdSubjects;
      }
    }

    return undefined;
  }

  /**
   * Attaches a new Subject to roamingIdSubjects and returns the created Subject.
   *
   * If a Subject is already attached, the existing Subject is returned.
   */
  private addRoamingIdSubject(deviceId: string, username?: string): BehaviorSubject<number|undefined> {
    let subject = this.getRoamingIdSubject(deviceId, username);

    if (subject) {
      // Subject already exists
      return subject;
    }

    /** Use -1 as initial value to distinguish between initial value and no roaming group assigned */
    subject = new BehaviorSubject<number|undefined>(-1);

    if (username != null) {
      let deviceRoamingIdSubject = this.roamingIdSubjects.get(deviceId);

      if (!deviceRoamingIdSubject || !(deviceRoamingIdSubject instanceof Map)) {
        deviceRoamingIdSubject = new Map();
        this.roamingIdSubjects.set(deviceId, deviceRoamingIdSubject);
      }

      deviceRoamingIdSubject.set(username, subject);
    } else {
      this.roamingIdSubjects.set(deviceId, subject);
    }

    return subject;
  }

  public getRoamingHighest(): Observable<any> {
    return this.scgHttp.get<any>(environment.apiUrl + 'roaming/roamingmethodhighest');
  }

  public setRoamingHighest(isEnabled: boolean): Observable<any> {
    const postVal = {value: isEnabled};
    return this.scgHttp.withoutSync().post<any>(environment.apiUrl + 'roaming/roamingmethodhighest', postVal);
  }



}
